import { useEffect, useState } from 'react';
import { RICERCA_ARTICOLI, useArticoli } from '../api/articoli.js';
import { ErroreApi } from '../api/cliente.js';
import type { Ordine, RigaOrdineDettaglio } from '../api/ordini.js';
import {
  useAggiungiRigaOrdine,
  useListiniOrdine,
  useModificaRigaOrdine,
  usePrezzoProposto,
} from '../api/ordini-scrittura.js';
import { Modale } from '../componenti/Modale.js';
import { VoceArticolo } from '../componenti/VoceArticolo.js';
import { importo } from '../formati.js';
import type { RigaArticolo } from '../api/articoli.js';
import { t } from '../i18n/lingua.js';

/**
 * Una riga d'ordine.
 *
 * Il prezzo non lo si scrive: lo propone il listino. Si sceglie l'articolo e
 * il servizio risponde con il prezzo, il costo DDP, l'aliquota, gli sconti e
 * il riferimento del cliente — e se per la destinazione di **questo** ordine
 * esiste un prezzo dedicato, è quello.
 *
 * Resta modificabile: capita di dover fare un prezzo diverso, e impedirlo
 * costringerebbe a cambiare il listino per un ordine solo. Ma quando il
 * prezzo viene da un'eccezione per destinazione lo si dice, perché è
 * l'informazione che fa capire perché è diverso da quello che ci si aspetta.
 */
export function ModaleRigaOrdine({
  ordine,
  riga,
  onChiudi,
}: {
  ordine: Ordine;
  /** Nullo per una riga nuova. */
  riga: RigaOrdineDettaglio | null;
  onChiudi: () => void;
}) {
  const [cerca, setCerca] = useState('');
  const [articoloId, setArticoloId] = useState<number | null>(riga?.articleId ?? null);
  const [articoloScelto, setArticoloScelto] = useState<RigaArticolo | null>(null);
  const [listinoId, setListinoId] = useState<number | null>(riga?.pricelistId ?? null);
  const [campi, setCampi] = useState({
    quantity: riga ? String(riga.quantity) : '',
    priceExw: riga?.priceExw ?? '',
    costDdp: riga?.costDdp ?? '0',
    crossReference: riga?.crossReference ?? '',
    discountPercentage1: riga?.discountPercentage1 ?? '',
    discountPercentage2: riga?.discountPercentage2 ?? '',
    discountPercentage3: riga?.discountPercentage3 ?? '',
  });
  const [taxId, setTaxId] = useState<number | null>(null);

  const listini = useListiniOrdine(ordine.id, true);
  const aggiungi = useAggiungiRigaOrdine(ordine.id);
  const modifica = useModificaRigaOrdine(ordine.id);

  // il listino in vigore si propone da solo, una volta: è quasi sempre quello
  // giusto. Prima tornava anche dopo aver scelto «Nessuno», che così non si
  // poteva scegliere
  const [listinoProposto, setListinoProposto] = useState(false);
  useEffect(() => {
    if (listinoProposto || !listini.data) return;
    setListinoProposto(true);
    if (listinoId !== null) return;
    const inVigore = listini.data.pricelists.find((l) => l.current) ?? listini.data.pricelists[0];
    if (inVigore) setListinoId(inVigore.id);
  }, [listini.data, listinoId, listinoProposto]);

  /**
   * L'aliquota predefinita dell'azienda, finché il listino non ne propone
   * una: senza listino è quella che resta. Un quarto delle righe dal 2025
   * non ha listino, quasi tutte esenti.
   */
  useEffect(() => {
    if (riga || taxId !== null || !listini.data) return;
    const predefinita = listini.data.taxes.find((a) => a.isDefault) ?? listini.data.taxes[0];
    if (predefinita) setTaxId(predefinita.id);
  }, [listini.data, riga, taxId]);

  const proposto = usePrezzoProposto(ordine.id, listinoId, articoloId);

  /**
   * Quando arriva un prezzo dal listino si riempiono i campi. Solo su una
   * riga nuova: su una esistente sovrascriverebbe un prezzo che qualcuno ha
   * scelto a mano.
   */
  useEffect(() => {
    const p = proposto.data?.price;
    if (!p || riga) return;
    setCampi((c) => ({
      ...c,
      priceExw: p.priceExw,
      costDdp: p.costDdp,
      crossReference: p.crossReference ?? '',
      discountPercentage1: p.discountPercentage1 ?? '',
      discountPercentage2: p.discountPercentage2 ?? '',
      discountPercentage3: p.discountPercentage3 ?? '',
    }));
    setTaxId(p.taxId);
  }, [proposto.data, riga]);

  /**
   * Scelto un listino, si cerca solo fra i suoi articoli, e si vedono subito
   * senza scrivere: un listino ne ha di solito due, quasi mai più di sedici
   * (Rita, 30 ottobre 2026). Senza listino si cerca in tutto il catalogo,
   * da due lettere in su, come prima.
   */
  const conListino = listinoId !== null;
  const cerco = conListino || cerca.trim().length >= 2;
  const trovati = useArticoli(
    {
      ...RICERCA_ARTICOLI,
      q: cerca,
      size: conListino ? 50 : 20,
      filters: conListino ? { pricelistId: listinoId } : {},
    },
    !riga && cerco,
  );
  const elenco = cerco ? (trovati.data?.rows ?? []) : [];
  const altri = cerco && trovati.data ? trovati.data.total - elenco.length : 0;

  const cambia = (chiave: keyof typeof campi, valore: string) =>
    setCampi((c) => ({ ...c, [chiave]: valore }));

  const inCorso = aggiungi.isPending || modifica.isPending;
  const errore =
    aggiungi.error instanceof ErroreApi
      ? aggiungi.error.message
      : modifica.error instanceof ErroreApi
        ? modifica.error.message
        : null;

  const numero = (v: string) => (v === '' ? null : Number(v));

  /** Il totale, calcolato come lo calcola il servizio: si vede prima di salvare. */
  const anteprima = (() => {
    const q = Number(campi.quantity || 0);
    if (q <= 0) return null;
    let totale = (Number(campi.priceExw || 0) + Number(campi.costDdp || 0)) * q;
    for (const s of [campi.discountPercentage1, campi.discountPercentage2, campi.discountPercentage3]) {
      const p = Number(s || 0);
      if (p === 0) continue;
      totale = Number((totale - (totale / 100) * p).toFixed(2));
    }
    return totale.toFixed(2);
  })();

  const conferma = () => {
    const corpo: Record<string, unknown> = {
      quantity: Number(campi.quantity),
      priceExw: Number(campi.priceExw || 0),
      costDdp: Number(campi.costDdp || 0),
      crossReference: campi.crossReference.trim() || null,
      discountPercentage1: numero(campi.discountPercentage1),
      discountPercentage2: numero(campi.discountPercentage2),
      discountPercentage3: numero(campi.discountPercentage3),
      ...(listinoId ? { pricelistId: listinoId } : {}),
    };

    if (riga) {
      modifica.mutate({ lineId: riga.id, ...corpo }, { onSuccess: onChiudi });
    } else {
      if (!taxId) return;
      aggiungi.mutate({ articleId: articoloId, taxId, ...corpo }, { onSuccess: onChiudi });
    }
  };

  const senzaAliquota = !riga && !taxId;
  const pronto =
    campi.quantity !== '' &&
    Number(campi.quantity) > 0 &&
    (riga !== null || (articoloId !== null && !senzaAliquota));

  return (
    <Modale
      titolo={riga ? t('Modifica la riga') : t('Aggiungi un articolo all’ordine')}
      occhiello={ordine.orderNumber}
      larga
      inCorso={inCorso}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={inCorso}>
            {t('Annulla')}
          </button>
          <button className="btn btn-primary" onClick={conferma} disabled={!pronto || inCorso}>
            {inCorso ? t('Salvo…') : t('Salva')}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}

      <div className="fields" style={{ marginBottom: 14 }}>
        <div className="field span-2">
          <label htmlFor="ro-listino">{t('Listino da cui prendere il prezzo')}</label>
          <select
            id="ro-listino"
            className="select"
            value={listinoId ?? ''}
            onChange={(e) => setListinoId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">{t('Nessuno — prezzo a mano')}</option>
            {(listini.data?.pricelists ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
                {' '}({l.current ? t('in vigore') : t('scaduto')})
              </option>
            ))}
          </select>
          {listini.data && listini.data.pricelists.length === 0 && (
            <span className="dim" style={{ fontSize: 11 }}>
              {t('Questo cliente non ha listini: il prezzo si scrive a mano.')}
            </span>
          )}
        </div>
        {!riga && (
          <div className="field">
            <label htmlFor="ro-aliquota">
              {t('Aliquota')}<span className="req">*</span>
            </label>
            <select
              id="ro-aliquota"
              className="select"
              value={taxId ?? ''}
              onChange={(e) => setTaxId(e.target.value ? Number(e.target.value) : null)}
            >
              {(listini.data?.taxes ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {riga ? (
        <div className="fields" style={{ marginBottom: 14 }}>
          <div className="field span-2">
            <label>{t('Articolo')}</label>
            <div className="readonly">
              <VoceArticolo articolo={{ ...riga, id: riga.articleId ?? riga.id }} />
            </div>
          </div>
        </div>
      ) : (
        <div className="field span-2" style={{ marginBottom: 14 }}>
          <label htmlFor="ro-cerca">
            {t('Articolo')}<span className="req">*</span>
            <span className="dim" style={{ fontWeight: 400 }}>
              {' '}
              {t('— cerca per descrizione, SKU o numero BC')}
            </span>
          </label>
          {articoloId !== null && articoloScelto ? (
            <div className="scelto">
              <VoceArticolo articolo={articoloScelto} />
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setArticoloId(null);
                  setArticoloScelto(null);
                }}
              >
                {t('Cambia')}
              </button>
            </div>
          ) : (
            <>
              <input
                id="ro-cerca"
                className="input"
                placeholder={conListino ? t('Scrivi per restringere l’elenco') : t('Scrivi almeno due lettere')}
                value={cerca}
                autoFocus
                onChange={(e) => setCerca(e.target.value)}
              />
              {conListino && (
                <span className="dim" style={{ fontSize: 11 }}>
                  {t('Solo gli articoli del listino scelto. Per cercare in tutto il catalogo, scegli «Nessuno — prezzo a mano».')}
                </span>
              )}
              {conListino && trovati.data && !trovati.isFetching && elenco.length === 0 && (
                <p className="dim" style={{ fontSize: 12, margin: '6px 0 0' }}>
                  {cerca.trim()
                    ? t('Nessun articolo del listino per «{cerca}».', { cerca: cerca.trim() })
                    : t('Questo listino non ha articoli.')}
                </p>
              )}
              {elenco.length > 0 && (
                <ul className="risultati">
                  {elenco.map((a) => (
                    <li key={a.id}>
                      <button
                        className="risultato"
                        onClick={() => {
                          setArticoloId(a.id);
                          setArticoloScelto(a);
                          setCerca('');
                        }}
                      >
                        <VoceArticolo articolo={a} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {altri > 0 && (
                <p className="dim" style={{ fontSize: 11, margin: '4px 0 0' }}>
                  {t('Altri {n}: scrivi per restringere.', { n: altri })}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* da dove viene il prezzo: è l'informazione che spiega perché è quello */}
      {proposto.data?.price && (
        <p
          className={proposto.data.price.fromDestination ? 'nota-prezzo' : 'dim'}
          style={proposto.data.price.fromDestination ? undefined : { fontSize: 12, margin: '0 0 12px' }}
        >
          {proposto.data.price.fromDestination
            ? t('Prezzo dedicato a questa destinazione, non quello di riga: viene dal listino «{listino}».', {
                listino: proposto.data.price.pricelistName ?? '',
              })
            : t('Prezzo di listino, da «{listino}».', { listino: proposto.data.price.pricelistName ?? '' })}
        </p>
      )}
      {listinoId !== null && articoloId !== null && proposto.data && !proposto.data.price && (
        <p className="dim" style={{ fontSize: 12, marginTop: 0, marginBottom: 12 }}>
          {t('Questo articolo non è in quel listino: scrivi il prezzo a mano.')}
        </p>
      )}

      <div className="fields">
        <div className="field">
          <label htmlFor="ro-q">
            {t('Quantità')}<span className="req">*</span>
          </label>
          <input
            id="ro-q"
            className="input num"
            type="number"
            step="1"
            min="1"
            value={campi.quantity}
            onChange={(e) => cambia('quantity', e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="ro-prezzo">{t('Prezzo EXW')}</label>
          <input
            id="ro-prezzo"
            className="input num"
            type="number"
            step="0.001"
            min="0"
            value={campi.priceExw}
            onChange={(e) => cambia('priceExw', e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="ro-ddp">{t('Costo DDP')}</label>
          <input
            id="ro-ddp"
            className="input num"
            type="number"
            step="0.001"
            min="0"
            value={campi.costDdp}
            onChange={(e) => cambia('costDdp', e.target.value)}
          />
        </div>
        <div className="field span-2">
          <label htmlFor="ro-rif">{t('Riferimento del cliente')}</label>
          <input
            id="ro-rif"
            className="input"
            value={campi.crossReference}
            onChange={(e) => cambia('crossReference', e.target.value)}
          />
        </div>
        {([1, 2, 3] as const).map((n) => (
          <div className="field" key={n}>
            <label htmlFor={`ro-sc${n}`}>{t('Sconto {n} (%)', { n })}</label>
            <input
              id={`ro-sc${n}`}
              className="input num"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={campi[`discountPercentage${n}` as keyof typeof campi]}
              onChange={(e) =>
                cambia(`discountPercentage${n}` as keyof typeof campi, e.target.value)
              }
            />
          </div>
        ))}
      </div>

      {anteprima !== null && (
        <p className="anteprima">
          {t('Totale della riga:')} <strong>{importo(anteprima, ordine.currency)}</strong>
        </p>
      )}
    </Modale>
  );
}
